import React, { useEffect, useState, useCallback } from "react";
import { Download, X, Share, Plus, MoreVertical } from "lucide-react";

// ── Platform / install-state detection ──────────────────────────────────────────────
// `beforeinstallprompt` only fires on Chromium (Android + desktop Chrome/Edge). iOS Safari NEVER
// fires it — those users must use Share → Add to Home Screen. So we ALWAYS surface an Install
// button (unless already installed) and either trigger the native prompt or show step-by-step
// instructions for the user's platform.

// The event can fire before React mounts, so index.js stashes it on window — read it here too.
function getStashedPrompt() {
  return typeof window !== "undefined" ? window.__bipInstallPrompt || null : null;
}

function detectPlatform() {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || navigator.vendor || "";
  const isIOS = /iphone|ipad|ipod/i.test(ua) ||
    // iPadOS 13+ reports as Mac but is touch-capable
    (/macintosh/i.test(ua) && typeof document !== "undefined" && "ontouchend" in document);
  if (isIOS) return "ios";
  if (/android/i.test(ua)) return "android";
  return "desktop";
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export function useInstallState() {
  const [deferred, setDeferred] = useState(getStashedPrompt);
  const [installed, setInstalled] = useState(isStandalone);
  const platform = detectPlatform();

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      window.__bipInstallPrompt = e;
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
      window.__bipInstallPrompt = null;
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  return { deferred, installed, platform };
}

// Shared click handler: native prompt when available, else open instructions.
function useInstallAction() {
  const { deferred, installed, platform } = useInstallState();
  const [showHelp, setShowHelp] = useState(false);

  const trigger = useCallback(async () => {
    const prompt = deferred || getStashedPrompt();
    if (prompt) {
      try {
        prompt.prompt();
        await prompt.userChoice;
        window.__bipInstallPrompt = null;
      } catch {
        /* user dismissed */
      }
      return;
    }
    // No native prompt (iOS Safari, or Android where the event hasn't fired) → show instructions.
    setShowHelp(true);
  }, [deferred]);

  return { installed, platform, trigger, showHelp, setShowHelp };
}

function InstructionsModal({ platform, onClose }) {
  const steps = {
    ios: [
      { Icon: Share, text: "Tap the Share button in Safari's toolbar (the square with an upward arrow)." },
      { Icon: Plus, text: "Scroll down and tap “Add to Home Screen”." },
      { Icon: Download, text: "Tap “Add” — BREAKPOINT will appear on your home screen like an app." },
    ],
    android: [
      { Icon: MoreVertical, text: "Tap the ⋮ menu in the top-right of Chrome." },
      { Icon: Download, text: "Tap “Install app” (or “Add to Home screen”)." },
      { Icon: Plus, text: "Confirm — BREAKPOINT installs as a standalone app." },
    ],
    desktop: [
      { Icon: Download, text: "Click the install icon in the address bar (right side)." },
      { Icon: MoreVertical, text: "Or open the browser menu → “Install BREAKPOINT”." },
      { Icon: Plus, text: "Confirm to add it as a desktop app." },
    ],
    other: [
      { Icon: MoreVertical, text: "Open your browser menu." },
      { Icon: Download, text: "Choose “Install app” or “Add to Home screen”." },
    ],
  };
  const list = steps[platform] || steps.other;
  const note =
    platform === "ios"
      ? "Note: installing to the home screen only works in Safari on iPhone/iPad."
      : null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="install-instructions-modal"
    >
      <div
        className="w-full max-w-md bg-[#0f172a] border border-[#1e293b] rounded-2xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-[#10b981]/15 flex items-center justify-center">
              <Download size={20} className="text-[#10b981]" />
            </div>
            <span className="text-lg font-bold text-white">Install BREAKPOINT</span>
          </div>
          <button onClick={onClose} data-testid="install-modal-close" className="text-[#6b7280] hover:text-white">
            <X size={22} />
          </button>
        </div>
        <p className="text-sm text-[#9ca3af] mb-5">Add BREAKPOINT to your home screen to use it like a native app — fullscreen, fast, one tap away.</p>
        <div className="space-y-3.5">
          {list.map((s, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-8 h-8 shrink-0 rounded-lg bg-[#1e293b] flex items-center justify-center text-[#10b981] font-bold text-sm">{i + 1}</div>
              <div className="flex items-center gap-2 pt-1">
                <s.Icon size={16} className="text-[#9ca3af] shrink-0" />
                <span className="text-sm text-[#e2e8f0]">{s.text}</span>
              </div>
            </div>
          ))}
        </div>
        {note && <p className="text-[12px] text-[#f59e0b] mt-5">{note}</p>}
        <button
          onClick={onClose}
          data-testid="install-modal-got-it"
          className="w-full mt-6 py-3 rounded-xl bg-[#10b981] text-[#06281d] font-bold text-sm hover:bg-[#0ea371] transition-colors"
        >
          Got it
        </button>
      </div>
    </div>
  );
}

// Full-width pill used in the desktop/mobile sidebar. Always visible unless already installed.
export function InstallButton() {
  const { installed, platform, trigger, showHelp, setShowHelp } = useInstallAction();
  if (installed) return null;
  return (
    <>
      <button
        onClick={trigger}
        data-testid="install-app-btn"
        className="w-full flex items-center gap-3 px-4 py-3 mb-1 rounded-xl text-sm font-semibold text-[#10b981] hover:bg-[#10b981]/10 border border-[#10b981]/40 transition-colors"
      >
        <Download size={18} /> Install App
      </button>
      {showHelp && <InstructionsModal platform={platform} onClose={() => setShowHelp(false)} />}
    </>
  );
}

// Compact labelled button for the mobile top bar. Always visible unless already installed.
export function InstallIconButton() {
  const { installed, platform, trigger, showHelp, setShowHelp } = useInstallAction();
  if (installed) return null;
  return (
    <>
      <button
        onClick={trigger}
        data-testid="install-app-icon-btn"
        aria-label="Install BREAKPOINT"
        className="flex items-center gap-1.5 text-[13px] font-semibold text-[#10b981] bg-[#10b981]/10 border border-[#10b981]/40 rounded-lg px-2.5 py-1.5"
      >
        <Download size={16} /> Install
      </button>
      {showHelp && <InstructionsModal platform={platform} onClose={() => setShowHelp(false)} />}
    </>
  );
}

const BANNER_DISMISS_KEY = "bp_install_banner_dismissed";

// One-time, auto-showing, dismissible banner nudging mobile users to install the app on their
// first visit — so they don't have to find the button. Shows once (persisted in localStorage),
// only on small screens, and only when not already installed.
export function InstallBanner() {
  const { installed, platform, trigger, showHelp, setShowHelp } = useInstallAction();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (installed) return;
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(BANNER_DISMISS_KEY) === "1";
    } catch {
      /* localStorage unavailable */
    }
    if (dismissed) return;
    // Small delay so it appears after the app paints (feels intentional, not jarring).
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, [installed]);

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(BANNER_DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  if (installed || !visible) return null;

  return (
    <>
      <div
        className="md:hidden sticky top-0 z-40 flex items-center gap-3 px-4 py-3 bg-[#10b981] text-[#06281d]"
        data-testid="install-banner"
      >
        <div className="w-9 h-9 shrink-0 rounded-lg bg-[#06281d]/15 flex items-center justify-center">
          <Download size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-extrabold leading-tight">Install BREAKPOINT</p>
          <p className="text-[11px] font-medium opacity-80 leading-tight">Add to your home screen — faster, fullscreen.</p>
        </div>
        <button
          onClick={() => { trigger(); }}
          data-testid="install-banner-cta"
          className="shrink-0 text-[12px] font-bold bg-[#06281d] text-[#10b981] rounded-lg px-3 py-2"
        >
          Install
        </button>
        <button onClick={dismiss} data-testid="install-banner-dismiss" aria-label="Dismiss" className="shrink-0 text-[#06281d]/70 hover:text-[#06281d]">
          <X size={20} />
        </button>
      </div>
      {showHelp && <InstructionsModal platform={platform} onClose={() => setShowHelp(false)} />}
    </>
  );
}
